// §4.1 — `HangarAPI`, the single app-facing surface (D-006).
//
// Two implementations exist: `kernel/api/inproc.ts` (direct) and `kernel/api/client.ts`
// (marshalled over the §9.3 bridge). **Every method is async in both.** A synchronous
// in-process fast path would make the two transports diverge behaviorally and the paved road
// would stop being real.
import type { Alarm, Stopwatch, Weekday } from './alarms';
import type {
  Reminder,
  ReminderList,
  ReminderShare,
  ShareSubject,
  UserGroup,
} from './reminders';
import type { FileEntry, FsEvent, PickOpts, VersionEntry, VfsPath } from './vfs';
import type {
  Connection,
  DailyScore,
  DailyScoreEntry,
  HeadToHead,
  LiveGame,
  Message,
  MultiplayerGameId,
  PlayPayload,
  PlayResult,
  PlaySession,
  Presence,
  PresenceState,
  SocialEvent,
  Thread,
} from './social';
import type { SkinId, SoundId, Theme } from './skin';
import type { AppManifest, ManifestProblem, Permission } from './manifest';
import type { AcceptRule } from './transfer';
import type { AppIconSpec } from './icons';
import type { CaptureFrame, CaptureRect, CssRect } from './snip';
import type { WeatherPlace } from './places';

export interface MenuNode {
  id: string;
  label: string;
  /** A submenu. Mutually exclusive with a leaf's `accelerator`/`checked`. */
  items?: MenuNode[];
  accelerator?: string;
  enabled?: boolean;
  checked?: boolean;
  separatorBefore?: boolean;
}

export interface PayloadItem {
  mime: string;
  bytes?: Uint8Array;
  text?: string;
  path?: VfsPath;
}

/** §10 — the one shape every transfer path carries. */
export interface Payload {
  items: PayloadItem[];
}

export interface OpenWindowOpts {
  title?: string;
  width?: number;
  height?: number;
  arg?: Payload;
}

export type ExecFrame =
  | { kind: 'stdout'; text: string }
  | { kind: 'stderr'; text: string }
  | { kind: 'exit'; code: number };

export type UserIdentity = { kind: 'guest' } | { kind: 'user'; displayName: string };

export interface HangarEnv {
  appId: string;
  skin: SkinId;
  theme: Theme;
  locale: string;
  user: UserIdentity;
}

export interface NotificationInput {
  title: string;
  body?: string;
  actions?: string[];
}

/** Error codes an app can observe through the API, in either transport. */
export const API_ERROR_CODES = [
  'BAD_REQUEST',
  'FORBIDDEN',
  'NOT_FOUND',
  'QUOTA_EXCEEDED',
  'PERMISSION_REQUIRES_GESTURE',
  /** The user dismissed a shell-drawn picker or prompt. Not an error in the app (D-072). */
  'CANCELLED',
  'REV_CONFLICT',
  'UNSUPPORTED',
  'INTERNAL',
] as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export class HangarApiError extends Error {
  readonly code: ApiErrorCode;
  readonly details: Record<string, unknown>;

  constructor(code: ApiErrorCode, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = 'HangarApiError';
    this.code = code;
    this.details = details;
  }
}

export interface HangarFsApi {
  list(dir: VfsPath): Promise<FileEntry[]>;
  read(path: VfsPath): Promise<Uint8Array>;
  readText(path: VfsPath): Promise<string>;
  write(path: VfsPath, data: Uint8Array | string): Promise<FileEntry>;
  mkdir(path: VfsPath): Promise<FileEntry>;
  move(from: VfsPath, to: VfsPath): Promise<FileEntry>;
  /** Moves to Trash (§7.8) — never an immediate unlink. */
  remove(path: VfsPath): Promise<void>;
  versions(path: VfsPath): Promise<VersionEntry[]>;
  restore(path: VfsPath, versionId: string): Promise<FileEntry>;
  watch(dir: VfsPath, cb: (ev: FsEvent) => void): Promise<() => void>;
  /** Shell-drawn; the gesture IS the grant (§9.5). */
  pickOpen(opts?: PickOpts): Promise<FileEntry[]>;
  pickSave(opts?: PickOpts): Promise<FileEntry>;
}

export interface HangarWindowApi {
  setTitle(t: string): Promise<void>;
  setIcon(svg: string): Promise<void>;
  setDirty(d: boolean): Promise<void>;
  setBusy(b: boolean): Promise<void>;
  onCloseRequested(cb: () => Promise<'allow' | 'deny'>): void;
  /** `multiWindow` apps only. Resolves to the new window id. */
  open(opts?: OpenWindowOpts): Promise<string>;
  close(): Promise<void>;
}

export interface HangarMenuApi {
  publish(tree: MenuNode[]): Promise<void>;
  onCommand(cb: (id: string) => void): void;
}

export interface HangarNotifyApi {
  post(n: NotificationInput): Promise<string>;
  onAction(cb: (id: string, action: string) => void): void;
}

export interface HangarTransferApi {
  copy(p: Payload): Promise<void>;
  /** Requires a shell-routed gesture (§10.1). */
  readClipboard(): Promise<Payload>;
  share(p: Payload): Promise<'delivered' | 'cancelled'>;
  onReceive(cb: (p: Payload, token: string) => void): void;
}

export interface HangarDialogApi {
  alert(m: string): Promise<void>;
  confirm(m: string): Promise<boolean>;
  prompt(m: string, d?: string): Promise<string | null>;
}

export interface HangarSessionApi {
  save(blob: unknown): Promise<void>;
  load(): Promise<unknown>;
}

export interface HangarLaunchApi {
  app(appId: string, arg?: Payload): Promise<void>;
  /**
   * §9.2 — open `path` in the app registered for it, or in `appId` when the user chose one from
   * "Open with". The caller must be able to read the path itself; the new window is granted it.
   */
  openWith(path: VfsPath, appId?: string): Promise<void>;
  /**
   * The path this window was launched to open, or `null` for an ordinary launch. §4.1 gives no
   * other way for the app on the receiving end of "Open with" to learn what it is opening
   * (D-080) — §10.3 is explicit that Open with is *not* a content transfer.
   */
  openedPath(): Promise<VfsPath | null>;
}

export interface HangarExecApi {
  run(cmd: string, args: string[], cwd: string): AsyncIterable<ExecFrame>;
}

export interface HangarAPI {
  env(): Promise<HangarEnv>;
  fs: HangarFsApi;
  window: HangarWindowApi;
  menu: HangarMenuApi;
  notify: HangarNotifyApi;
  transfer: HangarTransferApi;
  dialog: HangarDialogApi;
  session: HangarSessionApi;
  launch: HangarLaunchApi;
  exec?: HangarExecApi;
}

// ── §9.1 — the other half of the contract: what an in-process built-in exports ─────────────
//
// This lives in `shared` because `apps/**` may import nothing else (§6.5), and a built-in has
// to be able to name the type it implements.

/** Manifest-shaped window defaults for a built-in, mirroring §9.2's `window` block. */
export interface InProcessWindowDefaults {
  width: number;
  height: number;
  minWidth: number;
  minHeight: number;
  resizable: boolean;
  multiWindow: boolean;
}

export const DEFAULT_WINDOW: InProcessWindowDefaults = {
  width: 820,
  height: 560,
  minWidth: 320,
  minHeight: 240,
  resizable: true,
  multiWindow: false,
};

export interface InProcessFileType {
  ext: readonly string[];
  mime?: readonly string[];
  role: 'editor' | 'viewer';
}

/**
 * §9.2 `jumpList` — the actions a skin offers when its taskbar entry is right-clicked. Declared
 * by the app, rendered by the shell, and fired back at the app (§9.3 `command`/`fire`).
 */
export interface JumpListEntry {
  id: string;
  label: string;
}

export interface InProcessApp {
  id: string;
  /** D-057 — the launcher, the bar and the Store all need a display name. */
  name: string;
  mount(container: HTMLElement, api: HangarAPI): void;
  /**
   * D-057: takes the container so a `multiWindow` built-in can tear down the right instance.
   * §9.1 writes this as `unmount()`; without the argument a second File Explorer window could
   * not be closed independently.
   */
  unmount(container: HTMLElement): void;
  window?: Partial<InProcessWindowDefaults>;
  /**
   * D-188 — what this app's icon should say. Omitted, the generator falls back to `keywords`
   * and then to an abstract mark; every built-in declares one, because "which of these twelve
   * is the calculator" should not be a guess.
   */
  icon?: AppIconSpec;
  /** Extra words the launcher should match on (§12). */
  keywords?: readonly string[];
  /**
   * §24.13 (D-225) — the launcher group this app belongs to, e.g. `'Arcade'`.
   *
   * Thirteen built-ins becomes twenty-four with the Arcade, and an ungrouped launcher of
   * twenty-four is a list rather than a menu. Ungrouped apps stay ungrouped: Connections and Chat
   * are utilities that happen to have been motivated by games, and someone looking for their
   * message history should not have to think of it as an arcade feature.
   */
  category?: string;
  /**
   * §29.1 (D-369) — the sub-group inside a category: **Cards · Boards · Action · Rhythm**.
   *
   * Twenty Arcade titles in one flat list stops being scannable, and a header is navigation rather
   * than new surface. Only the Arcade uses this today; the field is general because a category that
   * grows past a screenful will want the same thing and should not need a second mechanism.
   */
  group?: string;
  /**
   * §24.11 (D-200) — this app is hidden from anyone who is not signed in.
   *
   * It cannot be implemented by declining to register: `buildKernel()` runs synchronously inside
   * `useState` at first render, while `decideAccess()` resolves later in an effect, so identity is
   * simply not known when the app list is built. The registry therefore filters at **read** time,
   * through `visible()`, which is the single list the launcher, §12 search, the bar, the Store and
   * §5.6 restore all read.
   *
   * Every Arcade title sets this **except Solitaire** (D-202), which is the one game an anonymous
   * visitor can play.
   */
  requiresAuth?: true;
  /** §9.2 `fileTypes` — wired into the registry at M5.1. */
  fileTypes?: readonly InProcessFileType[];
  /** §9.2 `jumpList` — taskbar right-click actions (D-100). */
  jumpList?: readonly JumpListEntry[];
  /**
   * §10.2 / §10.3 `accepts` — what this app will take from a drop or a Share. The same field a
   * manifest declares, so the shell asks a built-in and an installed app the same question.
   */
  accepts?: readonly AcceptRule[];
  /**
   * §9.3 `command` / `fire{id}` — a jump-list entry or menu item the user chose. An external app
   * receives this over the bridge; an in-process app has no bridge, so it is a method, handed the
   * same `HangarAPI` `mount` was. Called on the app's most recently focused window (D-100).
   */
  onCommand?(commandId: string, api: HangarAPI, context: AppInstanceContext): void;
}

export function windowDefaults(app: InProcessApp): InProcessWindowDefaults {
  return { ...DEFAULT_WINDOW, ...app.window };
}

/**
 * Identifies the calling app instance — one per window, since §9.3 mints a capability token
 * per window. Lives here rather than in the kernel so `kernel/apps` can be written without
 * importing `kernel/api`: those two are otherwise a cycle, which §4.2 forbids and
 * `tests/arch/boundaries.test.ts` catches.
 */
export interface AppInstanceContext {
  appId: string;
  winId: string;
  instanceKey: string;
}

// ── D-059 — capabilities §11 grants to specific built-ins ──────────────────────────────────
//
// `HangarAPI` is closed and carries no settings, no installer and no bridge log — yet §11.6,
// §11.5 and §11.12 describe built-ins that need exactly those. §9.5's table already sets the
// precedent that §11 may grant a named built-in more than the generic surface.
//
// A capability is handed to one app at composition, through a factory the shell calls
// (`createSettingsApp(access)`), never through `mount`. So the signature of §9.1 is unchanged,
// an external app can never obtain one, and which app holds which capability is a single
// readable list in `App.tsx`.

export interface SettingDescriptor {
  id: string;
  label: string;
  /** The Settings page this belongs on (§11.6). */
  page: string;
  type: 'boolean' | 'number' | 'string' | 'enum' | 'json';
  options?: readonly string[];
}

export interface SkinChoice {
  id: SkinId;
  label: string;
}

export interface WallpaperChoice {
  id: string;
  label: string;
  /** A ready-to-use `background` value for the preview swatch. */
  css: string;
}

/** §11.6 — what the Settings built-in is allowed to do. */
/** §7.6 — the storage figures Settings shows. Measured, not chosen, so not a setting. */
export interface StorageReadout {
  usageBytes: number;
  quotaBytes: number;
  /** 0…1, or 0 where the browser reports no quota. */
  ratio: number;
  /** At or over the §7.6 warning threshold. */
  over: boolean;
  /** §7.8 — what emptying the Recycle Bin would give back. */
  trashItems: number;
  trashBytes: number;
}

/**
 * §11.2 / D-059 — what the Terminal needs that `HangarAPI` does not give it.
 *
 * Exactly one thing: `install <url>` is a §11.2 verb and there is no `apps` surface on the API,
 * deliberately — installing is not something an arbitrary app may do. Everything else the terminal
 * does (`ls`, `cat`, `open`, `share`) goes through the ordinary API like any other app's would.
 */
export interface TerminalShellAccess {
  /**
   * §7.1 — the current user's root. A shell has to be able to *print* where it is, and `env()`
   * gives an app the user's display name but deliberately not their id — so the one caller that
   * genuinely needs the path is told it, the same way Explorer is.
   */
  home(): VfsPath;
  /** D-025 — paste-a-URL install is disabled for guests, which in v1 is everyone. */
  canInstall(): boolean;
  installFromUrl(url: string): Promise<{ ok: boolean; problems: ManifestProblem[] }>;
}

/**
 * §11.7 / D-059 — what Photos needs beyond `HangarAPI`.
 *
 * One thing again: **Set as wallpaper** writes a *setting*, and §4.1 gives an app no way to touch
 * settings — rightly, since an app that could would own the desktop's appearance. Zoom, pan,
 * folder navigation, slideshow and Share all go through the ordinary API.
 */
export interface PhotosShellAccess {
  /** §14 — point the wallpaper at this file. The shell decides how to render it. */
  setWallpaper(path: VfsPath): void;
  /**
   * §11.7 — "folder navigation": the images beside this one.
   *
   * §9.5 grants an app the file the user opened, not its folder, and Photos genuinely needs the
   * folder — so the widening lives here, in the capability, and is **exactly one directory**: the
   * parent of a path the user already chose to open. Not the tree, not user space (D-169).
   */
  siblings(path: VfsPath): Promise<FileEntry[]>;
}

/**
 * §25.4 / D-059 — what Cascade needs beyond `HangarAPI`.
 *
 * Two numbers: DAS and ARR. They are §14 settings, and §4.1 gives an app no way to read settings —
 * rightly, since an app that could would know the user's whole desktop configuration. So the
 * widening is exactly two getters for two specific keys, granted to one title at composition.
 *
 * **Both are synchronous on purpose.** They are read once per tick inside the input mapper, so
 * that retuning DAS applies to the piece already in the air rather than at the next launch; an
 * async read there would either block the frame or be a cache with its own staleness bug.
 */
/**
 * §26.3 / §26.4 (D-059) — what the Connections app and the shell's invite card are handed.
 *
 * `HangarAPI` is closed (§4.1) and has no social surface, which is correct: an *external* app must
 * never be able to read who somebody knows. So this is a capability injected at composition by
 * `App.tsx`, exactly as the Store's and Explorer's are — it never travels through `mount`, so an
 * installed app cannot obtain one however it asks.
 */
export interface SocialShellAccess {
  /** The roster, grouped by the caller. */
  connections(): Promise<Connection[]>;
  /** Presence for accepted connections only (§26.2). */
  presence(): Promise<Presence[]>;
  /** Mint an invite link (§26.3). */
  invite(): Promise<{ token: string; expiresAt: number; link: string }>;
  /**
   * Redeem one. `already` is true for a second click on the same link.
   *
   * Carries the peer's `displayName` as well as their ref, because the only thing worth saying
   * after a successful redemption is a person's name — and the ref is `u_a1b2@hangar.ab8.org`.
   */
  accept(token: string): Promise<{ peer: string; displayName: string; already: boolean }>;
  remove(peer: string): Promise<void>;
  block(peer: string): Promise<void>;
  /** The §26.2 heartbeat. Called by the shell on a timer, not by the app. */
  heartbeat(status: PresenceState, gameId: string | null): Promise<void>;
  /** Ring when anything social changes. Returns the unsubscribe. */
  subscribe(listener: (event: SocialEvent) => void): () => void;
  /** §19.4 — the shell's confirm, so remove and block cannot skip it. */
  confirm(message: string): Promise<boolean>;
  /** The signed-in user's own ref, for rendering "you". */
  me(): string | null;
  /**
   * §26.4 / §26.7 — suggest a game to a connection (D-212).
   *
   * The shell does all of it: it creates the session, opens the game on this screen, and rings the
   * peer, whose *shell* draws the card (§26.4). Connections cannot open a window or draw on the
   * above-every-frame layer, and giving it either would be giving every built-in both.
   */
  suggest(gameId: MultiplayerGameId, peer: string): Promise<void>;
  /** §26.7 (D-210) — the games that can be suggested. The list is not the app's. */
  playableGames(): readonly MultiplayerGameId[];
  /**
   * §30.6 (D-369, D-371) — one day's daily board: the caller and their accepted connections.
   *
   * Standings lives **inside Connections** rather than in a fourteenth app, which is what the
   * wave-1 scoreboard's burial asked for — "revisit if the scores turn out to be social". They did.
   * The roster app already holds the roster, so it is the one place a board of your connections
   * costs no new surface.
   */
  dailyScores(gameId: string, day?: string): Promise<DailyScore[]>;
  /** §30.4 — today, UTC, from the shell's clock. The board's default day. */
  today(): string;
  /** §30.5 (D-371) — lifetime W-L-D against one connection, per game. Computed on read. */
  headToHead(peer: string): Promise<HeadToHead[]>;
  /**
   * §26.8 (D-586) — the games this player is in the middle of.
   *
   * Here rather than in a fourteenth app for §30.6's reason, which put Standings in this same
   * screen: the roster app already holds the roster, so a list of *who you are playing right now*
   * costs no new surface and lands next to the Suggest button that started every one of them.
   */
  liveGames(): Promise<LiveGame[]>;
  /**
   * §26.8 (D-586) — open the window on a session that is already running.
   *
   * The shell's job, exactly as `suggest` is: Connections cannot open a window, and a resume is the
   * same act as accepting a card with the accepting already done.
   */
  resumeGame(sessionId: string, gameId: MultiplayerGameId): void;
  /**
   * §26.8 (D-586) — end a game for both players, for good.
   *
   * The escape hatch that makes auto-resume safe: a session nobody is coming back to would
   * otherwise sit `active` for ever and keep re-attaching itself to a window that opens. Confirmed
   * through §19.4 like every other destructive verb this app has.
   */
  endGame(sessionId: string): Promise<void>;
}

/**
 * §26.5 (D-059) — what the Chat app is handed.
 *
 * The same argument as `SocialShellAccess`, one step sharper: `HangarAPI` is closed and an
 * *external* app must never be able to read somebody's messages. So this is injected at
 * composition, never travels through `mount`, and is granted to exactly one built-in.
 *
 * `subscribe` is the §26.6 doorbell, unfiltered — it rings for presence and invites too. The app
 * re-`GET`s on any of them, which costs one request and removes the alternative: a client that
 * decided which frames mattered would be a second place the event vocabulary is written down.
 */
export interface ChatShellAccess {
  /** One row per accepted connection, with unread counts (§26.5). */
  threads(): Promise<Thread[]>;
  /** One page, backwards. `before` is a message id from this thread; `limit` is capped at 100. */
  messages(peer: string, options?: { before?: string; limit?: number }): Promise<Message[]>;
  /** Post. Rejects — a 413 over 4096 characters, a 403 to a non-connection (§16). */
  send(peer: string, body: string): Promise<Message>;
  /** Either party's delete, which removes it for both (D-218). */
  remove(id: string): Promise<void>;
  /** Ring when anything social changes. Returns the unsubscribe. */
  subscribe(listener: (event: SocialEvent) => void): () => void;
  /** §19.4 — the shell's confirm, so deleting a message cannot skip it. */
  confirm(message: string): Promise<boolean>;
  /** The signed-in user's own ref, for telling "mine" from "theirs". */
  me(): string | null;
  /**
   * §13 (D-345) — a conversation the shell was asked to open, or `null`.
   *
   * Read **once**, at mount: it is a request, and one that stayed readable would re-open the same
   * thread every time the window remounted. `onOpenThread` covers the other half — a click while
   * the window is already open, when there is no mount to read it at.
   */
  pendingThread(): string | null;
  onOpenThread(listener: (peer: string) => void): () => void;
}

/**
 * §11.14 (D-059) — what Alarms & Stopwatch is handed.
 *
 * The app draws a schedule it does not own. `HangarAPI` could not give it one: an alarm has to
 * keep its time while the window is closed, and everything in §4.1 dies with the window. So the
 * kernel keeps the schedule and this is the app's window onto it — read a snapshot, subscribe, and
 * ask for changes.
 *
 * There is deliberately no `tick`: the app runs its own display clock, because *how often the
 * digits move* is a rendering decision and the schedule has no opinion about it.
 */
export interface AlarmShellAccess {
  snapshot(): { alarms: readonly Alarm[]; stopwatch: Stopwatch; ringing: readonly string[] };
  subscribe(listener: () => void): () => void;
  add(fields: { label: string; hour: number; minute: number; days?: readonly Weekday[] }): void;
  remove(id: string): void;
  toggle(id: string, enabled: boolean): void;
  snooze(id: string): void;
  dismiss(id: string): void;
  startStopwatch(): void;
  stopStopwatch(): void;
  lapStopwatch(): void;
  resetStopwatch(): void;
  /** §19.4 — the shell's confirm, so deleting an alarm cannot skip it. */
  confirm(message: string): Promise<boolean>;
}

/**
 * §27 (D-059) — what the Reminders app is handed.
 *
 * Wider than the other capabilities because the app is wider: it draws a document two people share,
 * so it needs the lists, the items, the shares **and** the groups the shares are made of. What it
 * still cannot do is anything §4.1 would have let an external app do — read who somebody knows, or
 * reach a list it was not given.
 *
 * `connections` is here rather than the app asking `SocialShellAccess` for it: the sharing panel
 * needs the roster to offer names, and granting two capabilities to one app would make "what can
 * Reminders see" a question with two answers.
 */
export interface RemindersShellAccess {
  lists(): Promise<ReminderList[]>;
  createList(name: string, hue?: number): Promise<ReminderList>;
  renameList(id: string, name: string): Promise<ReminderList>;
  recolourList(id: string, hue: number): Promise<ReminderList>;
  deleteList(id: string): Promise<void>;

  items(listId: string): Promise<Reminder[]>;
  addItem(
    listId: string,
    fields: { title: string; notes?: string; dueAt?: number | null },
  ): Promise<Reminder>;
  updateItem(
    id: string,
    patch: {
      title?: string;
      notes?: string;
      dueAt?: number | null;
      completed?: boolean;
      position?: number;
    },
  ): Promise<Reminder>;
  deleteItem(id: string): Promise<void>;

  shares(listId: string): Promise<ReminderShare[]>;
  share(listId: string, kind: ShareSubject, id: string, canEdit: boolean): Promise<ReminderShare[]>;
  unshare(listId: string, kind: ShareSubject, id: string): Promise<void>;

  groups(): Promise<UserGroup[]>;
  createGroup(name: string): Promise<UserGroup>;
  deleteGroup(id: string): Promise<void>;
  addMember(groupId: string, ref: string): Promise<UserGroup>;
  removeMember(groupId: string, ref: string): Promise<void>;

  /** Accepted connections only (§26.3) — the people a list may be shared with. */
  connections(): Promise<Connection[]>;
  /** §26.6 — ring when anything social changes, including a `reminder` frame. */
  subscribe(listener: (event: SocialEvent) => void): () => void;
  /** §19.4 — the shell's confirm, so deleting a list cannot skip it. */
  confirm(message: string): Promise<boolean>;
  /** The signed-in user's own ref, for telling "you" from everybody else. */
  me(): string | null;
}

export interface ArcadeShellAccess {
  /** Milliseconds a direction must be held before it repeats. */
  dasMs(): number;
  /** Milliseconds between repeats once repeating. */
  arrMs(): number;
  /**
   * §30.4 — today, as `YYYY-MM-DD` in UTC.
   *
   * The daily deal's seed is derived from this date (§28.2), and a game may not read a clock:
   * §24.4's ban is enforced by a source scan over every title's directory, and it is enforced for a
   * reason — a title that could ask what time it is could produce a board that a replay could not
   * reproduce. So the date arrives from the shell, which is not bound by a rule about determinism,
   * exactly as `PlayShellAccess.now` does for co-op (D-380).
   */
  today(): string;
  /**
   * §30.4 (§28.2) — record a finished daily deal.
   *
   * **A game never fetches.** The shell service posts, because a title that could reach the network
   * is a title that could reach anything on it, and because the caller's ref belongs to the session
   * rather than to the game. Answers whether the row was stored — a worse attempt is accepted and
   * discarded, which is not a failure and must not read like one.
   *
   * Absent a server (a guest, an offline build) it resolves `false` rather than throwing: a daily
   * board is a nice-to-have and a game that crashed at the end of a won deal would not be.
   */
  postDailyScore(entry: DailyScoreEntry): Promise<boolean>;
}

/**
 * §26.7 (D-059) — what the two multiplayer titles are handed.
 *
 * `HangarAPI` carries nothing social and must not: an *external* app that could post a move would
 * be an external app inside somebody's game. So Conquest and Intercept receive this at composition,
 * as a prop, exactly as Cascade receives its two §14 numbers.
 *
 * A title never learns what a connection is, who is online, or that Chat exists. It gets one
 * session at a time and the four calls that session needs.
 */
export interface PlayShellAccess {
  /**
   * The session the shell wants this window to open on, or `null` for an ordinary solo launch.
   *
   * Read **once**, at mount, for `ChatShellAccess.pendingThread`'s reason: it is a request, and one
   * that stayed readable would rejoin the same game every time the window remounted.
   */
  pending(): { sessionId: string; gameId: string } | null;
  /** A join that arrived while the window was already open — the other half of `pending`. */
  onOpen(listener: (request: { sessionId: string; gameId: string }) => void): () => void;
  /**
   * §26.8 (D-586) — the running game of `gameId` this player is already in, or `null`.
   *
   * `pending()` is a *request*: the shell asking this window to open on a session, and it lives in
   * shell memory, so a reload erases it. The session does not go anywhere — the server still holds
   * it `active` — and this is how a window that came back from a restore finds it again.
   *
   * A title asks for its **own** game id and gets one session or nothing, so this is no wider than
   * `pending`: it cannot enumerate sessions, name a peer it was not already playing, or ask about a
   * game it is not.
   */
  resume(gameId: string): Promise<{ sessionId: string; gameId: string } | null>;
  read(sessionId: string, since?: number): Promise<PlaySession>;
  move(sessionId: string, seq: number, payload: PlayPayload): Promise<'ok' | 'conflict'>;
  /** §26.4 — the sender's window closing retracts the invitation, so a view calls this on unmount. */
  cancel(sessionId: string): Promise<void>;
  /**
   * §30.5 (D-371) — claim the verdict for a finished game.
   *
   * Posted by the client whose move ended it. Both clients compute the same ending from the same
   * log, so a race is idempotent in content and the 409 loser simply confirms — which is why this
   * answers a boolean rather than throwing.
   */
  postResult(sessionId: string, result: PlayResult): Promise<boolean>;
  /**
   * §30.6 (D-369) — play the same people again.
   *
   * One call to the shell path Suggest-a-game already uses (§26.3), pre-addressed to the peer of
   * `sessionId`: the shell mints a fresh session, opens it on this screen, and rings the other end,
   * whose *shell* draws the card. A game still cannot open a window, draw on the invitation layer,
   * or name anybody it was not already playing.
   */
  rematch(sessionId: string): Promise<void>;
  /** §26.6 — ring on any social frame; the view re-reads the log the `move` frames name. */
  subscribe(listener: (event: SocialEvent) => void): () => void;
  /** This client's own ref, for telling its moves from the peer's. */
  me(): string | null;
  /**
   * §26.8 F11 — is the other player still checking in?
   *
   * **One bit, not a status.** §26.2 already computes "have they been seen in the last 300
   * seconds", and the peer of a play session is an accepted connection by construction — so the
   * answer discloses nothing §26.9 was protecting. What a game must not learn is the *rest* of the
   * roster, and this cannot be asked about anybody else: the caller only has one ref, the one it
   * was handed.
   */
  peerHere(peer: string): Promise<boolean>;
  /**
   * Wall time.
   *
   * §24.4 bans the substrate from reading the clock and `boundaries.test.ts` pins the one file
   * that may (`gamekit/host.tsx`). A co-op session needs real seconds — the shared timeline is
   * one — so the clock arrives from the shell, which is not bound by a rule about determinism.
   */
  now(): number;
}

/**
 * §11.11 / D-059 — the Recycle Bin as a first-class app.
 *
 * It is the *same* four operations Explorer's capability carries, because §7.8 has one Trash and
 * a second implementation of "restore to the original path" is a second thing to get wrong. The
 * two destructive ones confirm through §19.4 inside the kernel, so neither caller can skip it.
 */
export interface RecycleBinShellAccess {
  /** Newest first. */
  list(): Promise<TrashItem[]>;
  restore(key: string): Promise<FileEntry>;
  deleteForever(key: string): Promise<boolean>;
  empty(): Promise<boolean>;
  usage(): Promise<{ items: number; bytes: number }>;
  /** Fires whenever the tree changes, so a restore in one window shows in another. */
  subscribe(listener: () => void): () => void;
}

/** §11.10 — one Center entry, as the calendar surface sees it. */
export interface CalendarNotice {
  readonly id: string;
  readonly appId: string;
  readonly title: string;
  readonly body: string | null;
  readonly at: number;
}

// ── §11.13 Console (D-191, D-059) ──────────────────────────────────────────────────────────

export type ConsoleLevel = 'debug' | 'info' | 'warn' | 'error';

/** One line. Flattened at the source: the Console renders text and never an object. */
export interface ConsoleEntry {
  readonly seq: number;
  readonly at: number;
  readonly source: string;
  readonly level: ConsoleLevel;
  readonly appId?: string;
  readonly message: string;
  readonly detail?: string;
}

/**
 * §11.13 — a place log lines come from.
 *
 * Deliberately the same shape as §11.2's `CommandProvider`: a provider is asked for what it has
 * and for a way to be told there is more, and the Console is not told where "there" is. Today
 * every source is the shell's own (D-191). Tomorrow one is an app streaming its container's
 * logs over the bridge, and the Console does not change — which is the whole reason this is an
 * interface rather than four hard-wired reads.
 */
export interface ConsoleSource {
  readonly id: string;
  readonly label: string;
  /** Everything held, oldest first. */
  entries(): readonly ConsoleEntry[];
  subscribe(onChange: () => void): () => void;
  /** Present when the source can be emptied; absent for one that is only ever read. */
  clear?(): void;
}

/**
 * §11.13 / D-059 — the Console's capabilities.
 *
 * Reading the shell's diagnostics is a **grant**, not something §4.1 offers: `logs` would let
 * any installed app watch every other app's failures, its sync state and its file paths. So it
 * lives here, given to one built-in at composition, exactly as the Inspector's bridge log does.
 */
export interface ConsoleShellAccess {
  sources(): readonly ConsoleSource[];
  /**
   * §11.13 — write the current view to a file under the user's home and answer its path.
   *
   * Export rather than persist (D-191): a log that wrote itself to the VFS would be a growing
   * file inside the filesystem it reports on, and every write would log a write.
   */
  export(text: string): Promise<string>;
}

/**
 * §11.10 / D-059 — Clock & Calendar's capability: **the calendar surface notifications post to**.
 *
 * §4.1's `notify` can post and can hear its own actions; it deliberately cannot read the Center,
 * because that would let any app read every other app's notifications. This app is the one §11
 * names as the surface they land on, so the read lives here where the grant is visible.
 */
export interface ClockShellAccess {
  notifications(): readonly CalendarNotice[];
  subscribe(listener: () => void): () => void;

  // ── §34.11 (M64.4, D-565) — the calendar half.
  //
  // Three calls, and none of them is "schedule a reminder": the shell derives every reminder instant
  // from the events file itself (`kernel/calendar/`), so the app's only job is to *save the file*. That
  // is deliberate — an app that armed its own reminders could not arm the next occurrence of a weekly
  // one, because its window is shut by then.

  /**
   * Tell the shell the calendar changed, so a reminder edited a second ago is armed a second later
   * rather than on the next tick that happens to re-read the file.
   *
   * A hint, not a handoff: the shell re-reads the file rather than trusting anything passed in.
   */
  calendarChanged(): void;
  /**
   * The day a clicked reminder notification asked to focus, or `null` (§34.11 — "clicking focuses the
   * day"). Read once on mount, the way `pendingChatThread` is (D-214's shape): the window may not
   * exist when the notification is clicked, so the request has to survive the gap.
   */
  pendingDay(): string | null;
  /** Subscribe to later clicks, for a window that is already open. */
  onOpenDay(listener: (dayKey: string) => void): () => void;
}

/**
 * §11.8 / D-059 — the PDF reader's one capability: **Download**.
 *
 * §4.1 has no way to put a file on the host OS — §7.7 makes that a shell act, because the shell
 * owns the one blob URL a download is staged on. Explorer reaches it the same way.
 */
export interface PdfShellAccess {
  download(path: VfsPath): Promise<void>;
}

// ── §34.2 Archive (D-059, D-509) ───────────────────────────────────────────────────────────

/**
 * §34.2 — Archive's one capability: **write extracted members beside the archive**.
 *
 * §9.5 grants a window the *file* it was opened on and explicitly not the directory containing
 * it, so "Extract Here" is impossible through `HangarAPI.fs` — the app is refused on every
 * sibling write (D-509). This is the narrowest thing that makes it work: the shell resolves the
 * destination from the opened path itself and confines every call under it, so the app names a
 * relative member and never a destination. It cannot reach a second directory, and it cannot
 * read anything at all.
 */
export interface ArchiveShellAccess {
  /** Names already present under `dir`, so a collision can be numbered rather than overwritten. */
  namesIn(dir: VfsPath, relative: string): Promise<string[]>;
  /** Create a directory under the opened archive's folder. */
  mkdir(dir: VfsPath, relative: string): Promise<void>;
  /** Write one extracted member under the opened archive's folder. */
  write(dir: VfsPath, relative: string, data: Uint8Array): Promise<void>;
}

// ── §34.6 Media (D-059, D-520 · M60) ───────────────────────────────────────────────────────

/**
 * §34.6 — Media's one capability: **§15's global mute**.
 *
 * §34.6 requires that "volume multiplies the global mute state (§15's mute reaches it)" and §4.1
 * gives an app no way to learn it: the mute is the §14 setting `sound.muted`, `HangarAPI` carries
 * no settings surface by design (D-059), and the sound *service* is deliberately not in the path
 * because these are the app's own `<audio>`/`<video>` elements rather than §15 cues — routing a
 * two-hour film through the cue player would give it the envelope of a notification chime.
 *
 * So the mute arrives the way Cascade's two §14 numbers arrive (§25.4): read out here, handed in at
 * composition. §34.6 names no capability holder for Media, which is the gap D-520 records — the
 * requirement is in the spec and the mechanism for it is not.
 *
 * Two calls, because one is not enough: `muted()` answers the question at render, and
 * `onMuteChange` is what makes muting the desktop *during* playback silence it at once instead of
 * at the next unrelated re-render.
 */
export interface MediaShellAccess {
  /** §14 `sound.muted`. */
  muted(): boolean;
  /** Fires on every change to the mute; returns the unsubscribe. */
  onMuteChange(listener: (muted: boolean) => void): () => void;
  /**
   * §34.6 — the opened file's **folder siblings of a kind Media claims**.
   *
   * The second thing §34.6 asks for that `HangarAPI` cannot do, and found by the e2e rather than by
   * reading (D-526). §9.5 grants a window opened by double-click the *file* and explicitly not the
   * directory around it — "a granted file does not" carry a subtree — so `api.fs.list(parentOf(path))`
   * is refused on every launch and the queue silently contains one item. This is the same wall D-509
   * hit for Archive's Extract Here, and it takes the same answer.
   *
   * Narrow in the two ways that matter. The **shell** derives the directory from `opened`, so the app
   * names no directory and cannot reach a second one; and the result is filtered to extensions Media
   * registers, so the grant is not a general directory reader — a caller learns that four `.mp3`s sit
   * beside the file it was already given, and nothing else about the folder.
   */
  siblings(opened: VfsPath): Promise<FileEntry[]>;
  /**
   * §34.6 — the bytes of a media file, for the element's blob URL.
   *
   * The other half of `siblings`, and needed for the same reason (D-526). §9.5 grants a
   * double-clicked window **that one file**, so `HangarAPI.fs.read` can open the file Media was
   * launched on and *nothing else*: pressing **next** would be refused, and a §5.6 restore — a new
   * window, which never inherits a grant — could not reopen the item it is restoring. §34.6 requires
   * both, so the read has to come from somewhere, and this is it.
   *
   * Bounded by **Media's own file types**: the shell refuses any extension Media does not register,
   * so this is "read an audio or video file in user space", not "read a file". It cannot reach a
   * document, a note, a key, or `/Apps/<other>`. That is a real grant and it is written down as one
   * rather than being smuggled in as a wider `fs` root.
   */
  read(path: VfsPath): Promise<Uint8Array>;
}

// ── §34.3 / §34.4 / §34.5 — Workbench lane B: the trio, Quick Notes, Diff ──────────────────

/**
 * §34.3 — Notepad's capability, added for the markdown preview (D-541, D-542).
 *
 * Notepad held none until now, and both of these are things §4.1 has no route for at all rather
 * than conveniences on top of one:
 *
 * - **`mdDefaultView`** is a §14 setting, and settings are not on `HangarAPI` — an app cannot read
 *   one (the `ArcadeShellAccess` precedent, where Cascade's two tuning numbers arrive the same way).
 * - **`readImage`** exists because §9.5 grants a window the *file* it was opened on and not the
 *   folder around it, which is exactly D-509's hole seen from the reading side: `![](diagram.png)`
 *   beside the document is refused through `fs.read`. The shell does the read and confines it, so
 *   the app gains an answer about one file rather than reach.
 */
export interface NotepadShellAccess {
  /** §14 `notepad.mdDefaultView` — which pane a `.md` tab opens on. */
  mdDefaultView(): 'edit' | 'preview';
  /**
   * Read an image a markdown document refers to. `target` is the path exactly as written in the
   * document: relative paths resolve against the document's own folder, absolute ones are confined
   * to the user's own space. Rejects anything outside it, so a document from somebody else cannot
   * aim the preview at `/System`.
   */
  readImage(docPath: VfsPath, target: string): Promise<Uint8Array>;
  /**
   * Follow a VFS link from a document: resolve it the same way, then open it in its registered app.
   *
   * `launch.openWith` cannot do this — it requires the *caller* to be able to read the path first
   * (§9.5), which a relative link beside the document never is. This is the shell-surface form of
   * the same open: the click is the gesture, and the window that opens is the one granted the file.
   */
  openLink(docPath: VfsPath, target: string): Promise<void>;
}

/**
 * §34.4 — Quick Notes' capability, and it carries **exactly one grant**: the global chord.
 *
 * Registering it is the whole of it. No app may claim a key the shell arbitrates (§9.7) — that is
 * what makes the capture card work with a game focused, since a game never sees a reserved chord —
 * and no app may reach the keyboard service through `HangarAPI` at all. So the grant is "the
 * `notes.capture` action is armed, and it opens my capture card"; the shell owns the key, the app
 * owns the card, and the arming is revocable because the app's registration is what turned it on.
 *
 * Deliberately nothing else: no filesystem reach (the card writes through §9.5 like any app), no
 * window control, no read of what else is bound.
 */
export interface NotesShellAccess {
  /**
   * Arm the capture chord. Returns the un-arm, so a rebind is a re-registration rather than a
   * second binding. Re-registers itself when `notes.captureChord` changes.
   */
  registerCaptureChord(): () => void;
  /**
   * The one directory §34.4 puts notes in: `/Users/<user>/Documents/Notes`.
   *
   * **Not a grant — an answer** (D-547). The *reach* comes from §9.5's per-built-in root, which
   * §9.5 provides for exactly this ("the paths its spec names in §11"); this method exists only
   * because the path is otherwise undiscoverable: §7.1 names the user's root `/Users/<user>` and
   * §4.1's `env()` deliberately reports an identity rather than a user id. `ExplorerShellAccess`
   * and `PdfShellAccess` carry `home()` for the same reason, and this is narrower than either —
   * one directory rather than the whole of user space.
   */
  notesDir(): VfsPath;
}

/** §34.5 — one side of a Compare: which revision of which file, and its text. */
export interface DiffLaunchSide {
  path: VfsPath;
  /** Absent means the file as it is now, rather than a version of it. */
  versionId?: string;
  /**
   * The side's content.
   *
   * §34.5 describes the payload as `{path, versionId?}` alone; the text is here because a version's
   * bytes are **not reachable through `HangarAPI`** — `fs.versions` lists them and `fs.restore`
   * rolls back, but nothing reads one, so a path and an id are not enough for the receiver to fetch
   * (D-543). Carrying the content is also §10.3's own posture: content under a payload token, and
   * no filesystem grant handed across.
   */
  text: string;
}

/** §34.5 — what Explorer's **Compare** hands Diff through `launch.app('diff', arg)`. */
export interface DiffLaunchPayload {
  a: DiffLaunchSide;
  b: DiffLaunchSide;
}

/** The mime a `DiffLaunchPayload` travels under, as one `text` item (§10's payload shape). */
export const DIFF_PAYLOAD_MIME = 'application/x-hangar-diff+json';

/**
 * §34.4 — the payload the shell hands a freshly launched `notes` window to say *be a capture card*.
 *
 * A window shape rather than a launch flag: Quick Notes has three (the library, the card, a pinned
 * note), and `LaunchOptions` is the generic placement contract every app shares — an app-specific
 * boolean on it would be the first of six. The payload route already exists, already waits for the
 * window to mount, and is how the same app learns anything else it was launched with.
 */
export const NOTES_CAPTURE_MIME = 'application/x-hangar-notes-capture';

/**
 * §34.4 — the payload that tells a new `notes` window it is a **pinned note**, carrying which one.
 *
 * The path travels in the item's own `path` field rather than as JSON in `text`: §10's payload shape
 * already has a slot for "a file this refers to", and using it means the pin needs no encoding and
 * no parse.
 */
export const NOTES_PIN_MIME = 'application/x-hangar-notes-pin';
// ── §34.9 Voice Memos · §34.10 Snip · §34.12 Weather (D-059, Lane C: M62–M65) ──────────────

/**
 * §34.9 — Voice Memos' one capability: **which directory its memos live in**.
 *
 * It exists because of a gap `HangarAPI` has by design and no app can close for itself: `env()`
 * carries the user's *display name*, and §7.1's tree is rooted at their *id*. The two are different
 * strings, so an app genuinely cannot build a path into the user's own home — Explorer has had
 * `home()` on its capability since §11.1 for the same reason.
 *
 * Reach is **not** granted here. The writes go through `api.fs` and are confined by §9.5's
 * `extraRoots`, which names this exact directory for `voice` and nothing else — so the capability
 * answers *where*, the gateway decides *whether*, and neither can quietly widen the other (D-560).
 */
export interface VoiceShellAccess {
  dir(): VfsPath;
}

/** §34.10 — the three capture modes the picker offers, in the order the strip shows them. */
export type SnipMode = 'region' | 'window' | 'full';

/** What the shell's picker overlay answered. A `null` answer from `pick` is a cancel (§34.17). */
export interface SnipPick {
  /** CSS-pixel rect within the viewport. `null` for the full desktop, which needs no crop. */
  readonly rect: CssRect | null;
  /** The window the user pointed at, when the mode was `window`. */
  readonly windowId?: string;
}

/**
 * §34.10 (D-494, D-059) — Snip's capability, which is the whole mechanism; the app is chrome.
 *
 * Every method is something §4.1 cannot expose to an app without exposing it to *every* app:
 * `getDisplayMedia` on the shell's own document, the window manager's geometry, the shell's §15
 * sound set, and a write into a fixed folder of the user's home. An external app holding any of
 * these could photograph the desktop, which is exactly the reach D-059 exists to keep granted
 * rather than assumed.
 */
export interface SnipShellAccess {
  /**
   * One frame of this tab, then the track is stopped **before the promise resolves** (D-494) — so
   * the browser's "sharing this tab" state never outlives the capture that needed it.
   *
   * Rejects with `CANCELLED` when the user dismisses the browser's prompt (§34.17).
   */
  captureTab(): Promise<ImageBitmap>;
  /** Dim the desktop and draw the picker overlay **before** the capture prompt (§34.10). */
  pick(mode: SnipMode): Promise<SnipPick | null>;
  /**
   * A window's rect in capture-bitmap pixels: WM geometry × `devicePixelRatio`, scaled by
   * `capturedWidth / innerWidth`. The arithmetic is one pure function (`shared/src/snip.ts`).
   */
  windowRect(windowId: string, frame: CaptureFrame): CaptureRect | null;
  /** The same conversion for a rect the overlay returned in CSS pixels. */
  cssRect(rect: CssRect, frame: CaptureFrame): CaptureRect;
  /** The whole frame, for the *full desktop* mode — which needs no crop and says so. */
  wholeRect(frame: CaptureFrame): CaptureRect;
  /** §15's `screenshot` SoundId, finally fired (§34.10). */
  playShutter(): void;
  /** Where a snip is saved (§34.10). The same “where” vs “whether” split Voice uses. */
  dir(): VfsPath;
}

/** §34.12 — a saved weather location, as `weather.locations` stores it. */
export interface WeatherLocation {
  readonly name: string;
  readonly lat: number;
  readonly lon: number;
}

/** §34.8's normalized response — **ours, not the provider's**. Metric throughout (§34.12). */
export interface WeatherDay {
  readonly date: string;
  readonly code: number;
  readonly hiC: number;
  readonly loC: number;
  readonly precipPct: number;
}

export interface WeatherReport {
  readonly fetchedAt: number;
  readonly current: {
    readonly tempC: number;
    readonly code: number;
    readonly windKmh: number;
    readonly humidity: number;
  };
  readonly daily: readonly WeatherDay[];
}

/** What a forecast request answered. `unreachable` is §34.17's "never an error loop". */
export type WeatherResult =
  | { readonly kind: 'ok'; readonly report: WeatherReport }
  | { readonly kind: 'unreachable' };

/**
 * §34.8 (D-588) — what a place search answered.
 *
 * An empty `places` is a **successful** search that found nothing, and it is not the same thing as
 * `unreachable`: one means "no such place", the other means "we could not ask". §34.17's rule that
 * every unreachable-provider case reads alike does not extend to conflating them — they get
 * different sentences because they ask the user for different things.
 */
export type WeatherSearchResult =
  | { readonly kind: 'ok'; readonly places: readonly WeatherPlace[] }
  | { readonly kind: 'unreachable' };

/**
 * §34.12 (D-059) — Weather's capability: two §14 settings and the one server route.
 *
 * Modelled on `ArcadeShellAccess`, which reads two §14 numbers the same way and for the same reason
 * — §4.1 has no settings surface, deliberately, because an app that could read the settings store
 * could read every other app's preferences. `forecast` is here rather than a `fetch` in the app
 * because §34.8's route is the shell's business: the app hands over a latitude and a longitude and
 * never a URL, which is the property D-491 is about.
 */
export interface WeatherShellAccess {
  locations(): readonly WeatherLocation[];
  setLocations(locations: readonly WeatherLocation[]): void;
  units(): 'imperial' | 'metric';
  subscribe(listener: () => void): () => void;
  forecast(lat: number, lon: number): Promise<WeatherResult>;
  /** Browser geolocation, asked **only** from the button (§34.12). `null` when it refuses. */
  locate(): Promise<{ lat: number; lon: number } | null>;
  /**
   * §34.8 (D-588) — find places by name: `Hartford, CT`, `KHFD`, `London`.
   *
   * A *string* rather than a URL, for the same reason `forecast` takes two numbers: the app cannot
   * express where the search goes, so the property D-491 is about survives a second egress route.
   */
  search(query: string): Promise<WeatherSearchResult>;
  /**
   * §34.8 (D-589) — the reverse: what place is at this coordinate.
   *
   * `null` covers both "nothing is there" and "we could not ask", deliberately. The only caller
   * wants a *name* for a location it already has, and both answers mean the same thing to it —
   * there is no name, use the fallback. A result the caller cannot act on differently is not worth
   * a second shape.
   */
  place(lat: number, lon: number): Promise<WeatherPlace | null>;
}

// ── §11.6 Settings, the pages beyond Personalization (D-059) ───────────────────────────────

/** §11.6 Keyboard — one row of "view the active keymap; per-binding override". */
export interface KeyBindingView {
  /** The §6.4 shell action this binds to. */
  readonly id: string;
  /** What the page calls it. */
  readonly label: string;
  /** Canonical chord after overrides, e.g. `Meta+Shift+V`. */
  readonly chord: string;
  /** The same chord written for the host platform — `⌘⇧V` on a Mac (§6.4). */
  readonly display: string;
  /** The skin's own chord, so Reset has something to say. */
  readonly fallback: string;
  readonly overridden: boolean;
  /** §9.7 — claimed by the shell and forwarded by every bridged app. */
  readonly reserved: boolean;
}

export type BindingResult = 'ok' | 'invalid' | 'conflict' | 'browser';

/** §11.6 Default apps — one extension and who can open it (§9.2's registry). */
export interface FileTypeView {
  readonly ext: string;
  readonly candidates: readonly { appId: string; name: string; role: 'editor' | 'viewer' }[];
  /** The app that opens it now — the user's choice, or the registry's first candidate. */
  readonly current: string;
  readonly chosen: boolean;
}

/** §11.6 Apps — an installed app as the page shows it (§9.6). */
export interface InstalledAppView {
  readonly id: string;
  readonly name: string;
  readonly version: string;
  readonly origin: string;
  readonly granted: readonly string[];
  readonly requested: readonly string[];
  readonly health: string;
  readonly installedAt: number;
}

/** §11.6 Account (§8.3). `anonymousAccess` is `null` when there is no server to ask. */
export interface AccountView {
  readonly signedIn: boolean;
  readonly displayName: string | null;
  readonly isAdmin: boolean;
  readonly hasServer: boolean;
  readonly anonymousAccess: boolean | null;
}

/** §11.6 About — the version, and D-042's browser-capability statement. */
export interface AboutView {
  readonly version: string;
  readonly skin: string;
  /** Each capability the shell probed once at boot, with the words the page shows for it. */
  readonly capabilities: readonly { id: string; label: string; present: boolean; consequence: string }[];
}

export interface SettingsShellAccess {
  catalog(): SettingDescriptor[];
  get(id: string): unknown;
  set(id: string, value: unknown): void;
  reset(id: string): void;
  /** Skins that are actually mountable right now (§6.1, F13). */
  skins(): SkinChoice[];
  wallpapers(): WallpaperChoice[];
  /** §7.6 — the live storage readout. */
  storage(): Promise<StorageReadout>;
  /**
   * §15 — play one system sound, for the Sounds page's preview.
   *
   * The *only* way a per-event toggle means anything to a user is hearing what they are toggling,
   * and the click is also the gesture the autoplay policy wants (D-143). Fire and forget: §15 says
   * a sound never blocks the action that triggered it, and this one has no action to block.
   */
  playSound(id: SoundId): void;
  /** Subscribe to setting changes made elsewhere; returns an unsubscribe. */
  subscribe(listener: () => void): () => void;

  // ── Keyboard (§11.6) ────────────────────────────────────────────────────────────────────
  /** The active keymap, overrides applied, in the skin's own order. */
  keyBindings(): readonly KeyBindingView[];
  /**
   * Bind `action` to `chord`, or pass `null` to go back to the skin's own.
   *
   * It refuses rather than silently losing a binding: `conflict` when another action already
   * holds the chord, `browser` when the browser would take it first (§6.4), `invalid` when it is
   * not a chord at all.
   */
  setBinding(action: string, chord: string | null): BindingResult;
  /**
   * The canonical chord for a key event — how the page captures "press the keys you want".
   *
   * §6.4's parser is the kernel's, and an app may not import it (§6.5); a second implementation
   * in the Settings app is exactly the drift the single-parser rule exists to prevent.
   */
  chordOf(event: {
    key: string;
    /**
     * **Required, not optional.** §6.4's parser prefers the physical `code` over `key`, because
     * `Shift+9` is `(` on a US layout and something else elsewhere — a chord recorded from `key`
     * alone would be stored in a form no keypress ever produces.
     */
    code: string;
    ctrlKey?: boolean;
    metaKey?: boolean;
    shiftKey?: boolean;
    altKey?: boolean;
  }): string | null;

  // ── Default apps (§9.2) ─────────────────────────────────────────────────────────────────
  fileTypes(): readonly FileTypeView[];
  setDefaultApp(ext: string, appId: string | null): void;

  // ── Storage (§7.6, §7.7) ────────────────────────────────────────────────────────────────
  /** §7.7 — "Export everything". Non-negotiable: it exists for guests and users alike. */
  exportEverything(): Promise<void>;
  /** §11.6 — start this browser's guest session over. Confirms through §19.4 first. */
  resetGuestSession(): Promise<boolean>;

  // ── Apps (§9.6) ─────────────────────────────────────────────────────────────────────────
  installedApps(): readonly InstalledAppView[];
  uninstallApp(appId: string): Promise<boolean>;

  // ── Account (§8.3) ──────────────────────────────────────────────────────────────────────
  account(): Promise<AccountView>;
  signIn(): void;
  signOut(): Promise<void>;
  /** Admins only; the server refuses anyone else (§16, §19.3). Returns the value it settled on. */
  setAnonymousAccess(on: boolean): Promise<boolean | null>;

  // ── About (§11.6, D-042) ────────────────────────────────────────────────────────────────
  about(): AboutView;
}

/** §7.8 — one item in the Recycle Bin, as the File Explorer sees it. */
export interface TrashItem {
  /** Opaque handle; the encoding is §7.8's business, not the app's. */
  key: string;
  /** The name it had before it was deleted. */
  name: string;
  originalPath: VfsPath;
  isDir: boolean;
  size: number;
  deletedAt: number;
}

/**
 * §11.1 — what the File Explorer built-in is allowed to do beyond `HangarAPI` (D-059).
 *
 * `HangarAPI.fs.remove` puts a file in the Trash; getting one *back* is not on the app surface,
 * because §7.8's keys and sidecars are kernel bookkeeping no app should be reimplementing.
 * `home()` is here for the same reason: §7.1 names the user's root `/Users/<user>`, and `env()`
 * deliberately reports an identity rather than a user id.
 */
/** D-042 / §7.7 — the browser-capability facts an app is allowed to see. */
export interface HostCapabilities {
  dragOut: boolean;
  nativeOpenPicker: boolean;
  nativeSavePicker: boolean;
  directoryImport: boolean;
}

/** §9.2 / §11.1 — one entry in the "Open with" submenu. */
export interface OpenWithCandidate {
  appId: string;
  name: string;
  role: 'editor' | 'viewer';
  /** What a double-click would do. Exactly one candidate is the default, when there is one. */
  isDefault: boolean;
}

export interface ExplorerShellAccess {
  home(): VfsPath;
  /** §9.2's registry, best first. Empty for an extension nobody claims. */
  openWithCandidates(path: VfsPath): OpenWithCandidate[];
  /**
   * §7.5 — the bytes of one past version, so §11.1's scrubber can *show* what it would restore.
   * `HangarAPI.fs` can list versions and restore one, but not read one without writing it back.
   */
  readVersion(path: VfsPath, versionId: string): Promise<Uint8Array>;
  /** D-042 — what this browser can do, probed once at boot (§7.7). */
  capabilities(): HostCapabilities;
  /**
   * §34.6 / D-496 (M60.5) — the device-local threshold in MB, so a row over it can be badged
   * **"on this device"**.
   *
   * A number rather than a per-file boolean: Explorer already holds every row's `size`, and the
   * predicate that turns the two into an answer is `kernel/vfs/device-local.ts` — the *same*
   * function the sync client consults, which is what stops Explorer badging a file sync was
   * happily uploading. Read at render, so changing the §14 setting re-badges the open window.
   */
  syncThresholdMb(): number;
  /** §7.7 — import a host-OS drop into `dir`. The shell walks it; the writes are still confined. */
  importDrop(transfer: unknown, dir: VfsPath): Promise<{ files: number; failed: number }>;
  /** §7.7 — the export path that exists in every browser: hand the file to the download stack. */
  download(path: VfsPath): Promise<void>;
  /**
   * §10.2 — tell the shell a drag of these paths has begun, so it can track it across windows.
   *
   * Explorer keeps its own `dragstart` for what happens *inside* its window (moving a file into a
   * folder) and for the host-OS export; this is the other half, and the shell punches its overlay
   * out around the source window so the two never fight. The source window is the focused one —
   * a drag begins with a pointer-down, and a pointer-down inside a window focuses it (§5.3).
   */
  beginContentDrag(paths: readonly VfsPath[]): void;
  /**
   * §7.7 — prepare the `DownloadURL` a drag out of Hangar will carry, or `null` where the drag
   * would be inert (D-042) or the file is too large to stage. `dragstart` cannot await anything,
   * so this runs when the selection changes and the handler uses whatever it produced.
   */
  prepareDragOut(path: VfsPath, size: number): Promise<string | null>;
  trash: {
    /** Newest first. */
    list(): Promise<TrashItem[]>;
    /** Back to its original path (§7.8). Returns where it actually landed. */
    restore(key: string): Promise<FileEntry>;
    /** Confirms through §19.4 first; `false` means the user said no. */
    deleteForever(key: string): Promise<boolean>;
    empty(): Promise<boolean>;
  };

  // ── §34.13 (M66.3) — Send to connection ────────────────────────────────────────────────
  //
  // Two members, and the shape is the point: Explorer names a **path** and gets back an
  // **outcome**. It never sees the roster, never learns who was offered, and never learns who was
  // chosen beyond the display name in the sentence it prints — because a list of who somebody
  // knows is §26.9's business, and an app that could read it could tell you.
  //
  // The picker itself is shell-drawn for §19.4's reason, the same one the Share picker has: the app
  // that asked must not be able to influence who the user picks.

  /**
   * D-024 — whether the verb exists at all. **False for a guest**, who has no server, no roster
   * and no account to send from; the item is absent rather than shown and refused.
   *
   * Synchronous, like `capabilities()` and `StoreShellAccess.canInstall()`, because it decides
   * whether a *menu* has an item and a menu is built during a render.
   */
  canSendToConnection(): boolean;
  /**
   * Open the shell's roster picker and offer this file to whoever is chosen.
   *
   * `size` is passed rather than looked up: Explorer already has the entry, and `HangarFsApi` has
   * no `stat` (§4.1) — asking the shell to find out again would be a second listing for a number
   * the caller is holding.
   */
  sendToConnection(path: VfsPath, size: number): Promise<SendFileOutcome>;
}

/**
 * §34.13 — what *Send to connection* answers.
 *
 * One case per sentence PLAN §7.5 binds, so the app maps an outcome to a literal instead of
 * inspecting a status code. `cancelled` is not a failure (D-072's instinct: a dismissed
 * shell-drawn picker is a decision), and `device-local` is the one refusal that is about the
 * *file* rather than about the network.
 */
export type SendFileOutcome =
  | { kind: 'sent'; toName: string; name: string }
  | { kind: 'cancelled' }
  | { kind: 'device-local' }
  | { kind: 'not-connected' }
  | { kind: 'failed' };

// ── §11.12 App Inspector (D-059) ───────────────────────────────────────────────────────────
//
// "This is the tool that makes wrapping app #5 an afternoon." Everything it shows is something
// the kernel already knows and no app may read for itself — another app's manifest, the bridge's
// own message log, what a *different* app was granted. So it is a capability, exactly like
// Explorer's, and the shape below is the whole of what it can see.

/** One window of an installed app, and the §9.5 grants that window holds. */
export interface InspectorInstance {
  instanceKey: string;
  winId: string;
  /** §9.3 — `pending`, `bridged` or `unbridged` (F7), or `none` when no frame exists. */
  bridge: 'pending' | 'bridged' | 'unbridged' | 'none';
  /** Frames this connection has had dropped. */
  drops: number;
  /** §9.5 — paths a picker gesture granted this window. Never inherited by another (D-092). */
  grants: string[];
}

export interface InspectorApp {
  id: string;
  name: string;
  version: string;
  origin: string;
  entry: string;
  installedAt: number;
  /** §9.6 — dimmed at 50 % in the launcher when this is `unhealthy`. */
  health: 'ok' | 'unhealthy';
  failures: number;
  /** §9.4 — what it actually holds. */
  granted: Permission[];
  /** Asked for in the manifest and *not* granted: §19.4 has not been through yet, or was refused. */
  ungranted: Permission[];
  /** §9.2 re-run against the origin it was served from. Empty for a manifest that still validates. */
  problems: ManifestProblem[];
  /** The cached manifest, for the raw view. */
  manifest: AppManifest;
  instances: InspectorInstance[];
}

/** One line of §11.12's live bridge log. A dropped frame is a line too — that is the point. */
export interface InspectorFrame {
  at: number;
  instanceKey: string | null;
  appId: string | null;
  direction: 'in' | 'out';
  channel: string;
  type: string;
  /** Approximate payload bytes. Typed arrays are counted as bytes, not as JSON (D-132). */
  size: number;
  /** §9.3 — why this frame never happened. Absent for one that did. */
  dropped?: { reason: string; detail: string };
}

/** §11.12 — the result of validating a pasted manifest or URL. */
export interface InspectorValidation {
  ok: boolean;
  /** What was actually checked: parsed JSON, or a document fetched from a URL. */
  source: 'json' | 'url';
  /** The URL the origin rule was checked against (§9.2). */
  fetchedFrom: string;
  problems: ManifestProblem[];
  manifest?: AppManifest;
}

export interface InspectorShellAccess {
  apps(): InspectorApp[];
  /** The bridge log and its drops, merged in time order, newest last. */
  frames(limit?: number): InspectorFrame[];
  /** Live traffic: called whenever a frame lands or is dropped. Returns the unsubscribe. */
  subscribe(onChange: () => void): () => void;
  /**
   * §11.12 — "a manifest validator that accepts a pasted URL or JSON".
   *
   * A URL is fetched and checked against *itself*, which is the §9.2 rule that matters: a
   * manifest may only speak for the origin that served it.
   */
  validate(input: string): Promise<InspectorValidation>;
  /**
   * §34.3 (M57.4) — one row per open window, for the **Performance** tab.
   *
   * This is a capability rather than something the app does for itself because counting the DOM
   * under *another* window's `.win-host` means walking the shell's tree, which §6.5 puts out of an
   * app's reach and §9.4 would make a hole if it did not. The heap reading and the rAF rate are
   * **not** here on purpose: `performance.memory` and `requestAnimationFrame` are globals of the
   * document the app already runs in, so asking the shell for them would be ceremony.
   */
  windows(): InspectorWindowStat[];
  /**
   * §34.3 — close a window from its row, the way the user closing it would: the app is asked first
   * and may veto (§18 F9). An app cannot close another app's window through `HangarAPI`.
   */
  closeWindow(winId: string): void;
}

/** §34.3 — what the Performance tab knows about one open window. */
export interface InspectorWindowStat {
  winId: string;
  instanceKey: string;
  appId: string;
  /** The app's display name, so the row reads like the taskbar rather than like an id. */
  appName: string;
  title: string;
  /**
   * DOM nodes under this window's `.win-host`, or `null` for a bridged/external frame.
   *
   * `null` is the honest answer rather than 0 or 1: a cross-origin document's node count is not
   * ours to read, and §34.3 says such a row reads **"external frame"**.
   */
  nodes: number | null;
  /** `<canvas>` elements under the host — the number that explains a busy frame budget. */
  canvases: number;
  /** Whether this window is an external app's frame (§9.2). */
  external: boolean;
}

// ── §11.5 Store (D-040, D-059) ─────────────────────────────────────────────────────────────

/** One card. The catalog entry, plus what the shell knows about it right now. */
export interface StoreCard {
  id: string;
  name: string;
  description: string;
  origin: string;
  category: string;
  /**
   * §29.1 (D-369, D-434) — the sub-group header this card sits under, e.g. `'Cards'`.
   *
   * The catalog has carried it since D-369 and the Store's `sectionsOf` call has always read it;
   * what was missing was this field, so every card arrived with `group: undefined` and the
   * `"{category} · {group}"` branch in `Store.tsx` was unreachable. D-369's whole point is that the
   * launcher and the Store cannot disagree about which shelf a title is on, and they could not
   * agree while only one of them could see the answer.
   */
  group?: string;
  /** §9.4 — what the manifest asks for. Shown *before* installing, which is the point. */
  permissions: Permission[];
  /** An `<svg>` string: the installed icon if it is installed, otherwise the generated one. */
  icon: string;
  installed: boolean;
  /** §9.6 — set once it is installed and its origin has stopped answering. */
  unhealthy: boolean;
  /** The last install attempt's §9.2 problems, so a card can say why it would not install. */
  problems: ManifestProblem[];
}

export interface StoreShellAccess {
  cards(): StoreCard[];
  /** Fetch, validate and install from this card's origin (§9.2). */
  install(appId: string): Promise<{ ok: boolean; problems: ManifestProblem[] }>;
  /** §11.5 — confirms through §19.4 first; `false` means the user said no. */
  uninstall(appId: string): Promise<boolean>;
  /**
   * §11.5 / D-025 — paste-a-URL install. **Disabled for guests**, which in v1 is everyone: the
   * third-party tier is deferred, and the field exists so the deferral is visible rather than
   * mysterious. The label is PLAN-bound (`COPY.storePasteUrlGuest`).
   */
  canPasteUrl(): boolean;
  installFromUrl(origin: string): Promise<{ ok: boolean; problems: ManifestProblem[] }>;
  subscribe(onChange: () => void): () => void;
}

/**
 * The method table both transports are checked against (§4.1, M1.2/M8.4). `env` is a bare
 * method on the root; everything else is grouped. `exec` is optional and therefore excluded.
 */
export const HANGAR_API_SURFACE = {
  '': ['env'],
  fs: [
    'list',
    'read',
    'readText',
    'write',
    'mkdir',
    'move',
    'remove',
    'versions',
    'restore',
    'watch',
    'pickOpen',
    'pickSave',
  ],
  window: ['setTitle', 'setIcon', 'setDirty', 'setBusy', 'open', 'close'],
  menu: ['publish'],
  notify: ['post'],
  transfer: ['copy', 'readClipboard', 'share'],
  dialog: ['alert', 'confirm', 'prompt'],
  session: ['save', 'load'],
  launch: ['app', 'openWith', 'openedPath'],
} as const satisfies Record<string, readonly string[]>;

/** Callback registrars — present on both transports but not promise-returning. */
export const HANGAR_API_CALLBACKS = {
  window: ['onCloseRequested'],
  menu: ['onCommand'],
  notify: ['onAction'],
  transfer: ['onReceive'],
} as const satisfies Record<string, readonly string[]>;
